import { useCallback, useEffect, useRef } from "react";
import azureSignalR from "../services/azureSignalR";
import utils from "../utils";

// Global event emitter for SignalR events
class SignalREventEmitter {
  constructor() {
    this.listeners = new Map();
  }

  subscribe(eventName, callback) {
    if (!this.listeners.has(eventName)) {
      this.listeners.set(eventName, new Set());
    }
    this.listeners.get(eventName).add(callback);

    // Return unsubscribe function
    return () => {
      const eventListeners = this.listeners.get(eventName);
      if (eventListeners) {
        eventListeners.delete(callback);
        if (eventListeners.size === 0) {
          this.listeners.delete(eventName);
        }
      }
    };
  }

  emit(eventName, data) {
    const eventListeners = this.listeners.get(eventName);
    if (eventListeners) {
      eventListeners.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          utils.debug(
            `Error in SignalR event listener for ${eventName}:`,
            error,
          );
        }
      });
    }
  }

  getListenerCount(eventName) {
    return this.listeners.get(eventName)?.size || 0;
  }
}

// Singleton instance
export const signalREventEmitter = new SignalREventEmitter();

export const useSignalRReconnect = () => {
  return useCallback(() => azureSignalR.reconnectSignalR(), []);
};

/**
 * Custom hook for subscribing to SignalR events
 * @param {string} eventName - The SignalR event name to listen for
 * @param {function} callback - Function to call when event is received
 * @param {array} dependencies - Dependencies array for useEffect
 */
export const useSignalREvent = (eventName, callback, dependencies = []) => {
  const callbackRef = useRef(callback);

  // Update callback ref when it changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const wrappedCallback = (data) => {
      callbackRef.current(data);
    };

    return signalREventEmitter.subscribe(eventName, wrappedCallback);
  }, [eventName, ...dependencies]);
};
